<template>
  <button
    :disabled="disabledState"
    :autofocus="isAutofocus"
    :class="modifiers"
    type="button"
    class="dd-button"
    @blur="$emit('blur')"
    @focus="$emit('focus')"
    @click="evt => $emit('click', evt)"
  >
    <span class="dd-button__content">
      <slot /><i v-if="newIcon" class="ddi ddi-plus new-icon" />
    </span>
    <i v-if="isLoading" class="ddi ddi-spinner dd-button__loading-spinner" />
  </button>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import {
  availableTypeOptions,
  availableSizeOptions,
} from '@/types/button-style-options'

export default defineComponent({
  name: 'DdButton',

  props: {
    type: {
      type: String,
      default: 'default',
      validator: (value: string) => {
        return availableTypeOptions.includes(value)
      },
    },

    color: {
      type: String,
      default: 'grey',
    },

    size: {
      type: String,
      default: 'large',
      validator: (value: string) => {
        return availableSizeOptions.includes(value)
      },
    },

    isDisabled: {
      type: Boolean,
      default: false,
    },

    isAutofocus: {
      type: Boolean,
      default: false,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    newIcon: {
      type: Boolean,
      default: false,
    },

    fullWidth: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['blur', 'click', 'focus'],

  computed: {
    disabledState(): boolean {
      return this.isDisabled || this.isLoading
    },

    modifiers(): Array<string> {
      const baseSelector = 'dd-button--'

      // Colors
      const selectors = [
        baseSelector + this.size,
        baseSelector + this.type,
        baseSelector + this.color,
      ]

      // Loading
      if (this.isLoading) {
        selectors.push('dd-button--loading')
        selectors.push('dd-button--disabled')
      }

      if (this.isDisabled) {
        selectors.push('dd-button--disabled')
      }

      if (this.fullWidth) {
        selectors.push('dd-button__full-width')
      }

      return selectors
    },
  },
})
</script>

<style lang="scss">
$hover-darken-percentage: 5%;

.dd-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  font-weight: 500;
  color: var(--color-text-secondary);
  cursor: pointer;

  &__content {
    display: flex;
    align-items: center;
    white-space: nowrap;
  }

  &__full-width {
    width: 100%;

    @include media-breakpoint-down(sm) {
      height: auto;
      min-height: 40px;
      padding-top: 8px;
      padding-bottom: 8px;

      .dd-button__content {
        white-space: normal;
        text-align: center;
      }
    }
  }

  &:focus {
    outline: none;
  }

  /* Colors */
  &--grey {
    color: var(--color-text-secondary);
    background-color: var(--color-background-secondary);
    border-color: $color--gray-30;

    &:hover:enabled {
      filter: brightness(var(--hover-brightness));
    }
  }

  &--grey-transparent {
    color: var(--color-text-primary);
    background-color: rgba($color--gray-15, 0.24);
    border-color: rgba($color--gray-75, 0.5);

    &:hover:enabled {
      background-color: rgba($color--gray-15, 0.34);
    }
  }

  &--light-orange {
    color: $white;
    background-color: $light-orange;
    border-color: $light-orange;

    &:hover:enabled {
      background-color: darken($light-orange, $hover-darken-percentage);
    }
  }

  &--light-orange-transparent {
    color: $light-orange;
    background-color: rgba($light-orange, 0.24);
    border-color: rgba($light-orange, 0.24);

    &:hover:enabled {
      background-color: rgba($light-orange, 0.34);
    }
  }

  &--orange {
    color: $white;
    background-color: $reddish-orange;
    border-color: $reddish-orange;

    &:hover:enabled {
      background-color: darken($reddish-orange, $hover-darken-percentage);
    }
  }

  &--orange-transparent {
    color: $reddish-orange;
    background-color: rgba($reddish-orange, 0.24);
    border-color: rgba($reddish-orange, 0.24);

    &:hover:enabled {
      background-color: rgba($reddish-orange, 0.34);
    }
  }

  &--reddish-orange {
    color: $white;
    background-color: $reddish-orange;
    border-color: $reddish-orange;

    &:hover:enabled {
      background-color: darken($reddish-orange, $hover-darken-percentage);
    }
  }

  &--reddish-orange-transparent {
    color: $reddish-orange;
    background-color: rgba($reddish-orange, 0.24);
    border-color: rgba($reddish-orange, 0.24);

    &:hover:enabled {
      background-color: rgba($reddish-orange, 0.34);
    }
  }

  &--red {
    color: $white;
    background-color: $strawberry;
    border-color: $strawberry;

    &:hover:enabled {
      background-color: darken($strawberry, calc($hover-darken-percentage * 2));
    }
  }

  &--red-transparent {
    color: $strawberry;
    background-color: rgba($strawberry, 0.24);
    border-color: rgba($strawberry, 0.24);

    &:hover:enabled {
      background-color: rgba($strawberry, 0.34);
    }
  }

  &--strawberry {
    color: $white;
    background-color: $strawberry;
    border-color: $strawberry;

    &:hover:enabled {
      background-color: darken($strawberry, calc($hover-darken-percentage * 2));
    }
  }

  &--strawberry-transparent {
    color: $strawberry;
    background-color: rgba($strawberry, 0.24);
    border-color: rgba($strawberry, 0.24);

    &:hover:enabled {
      background-color: rgba($strawberry, 0.34);
    }
  }

  &--blue {
    color: $white;
    background-color: $color--blue;
    border-color: $color--blue;

    &:hover:enabled {
      background-color: darken($color--blue, $hover-darken-percentage);
    }
  }

  &--blue-transparent {
    color: $color--blue;
    background-color: rgba($color--blue, 0.24);
    border-color: rgba($color--blue, 0.24);

    &:hover:enabled {
      background-color: rgba($color--blue, 0.34);
    }
  }

  &--dodger-blue {
    color: $white;
    background-color: $color--blue;
    border-color: $color--blue;

    &:hover:enabled {
      background-color: darken($color--blue, $hover-darken-percentage);
    }
  }

  &--dodger-blue-transparent {
    color: $color--blue;
    background-color: rgba($color--blue, 0.24);
    border-color: rgba($color--blue, 0.24);

    &:hover:enabled {
      background-color: rgba($color--blue, 0.34);
    }
  }

  &--dark-teal {
    color: $white;
    background-color: $color--gray-90;
    border-color: $color--gray-90;

    &:hover:enabled {
      background-color: darken($color--gray-90, $hover-darken-percentage);
    }
  }

  &--dark-teal-transparent {
    color: var(--color-text-secondary);
    background-color: rgba($color--gray-90, 0.24);
    border-color: rgba($color--gray-90, 0.24);

    &:hover:enabled {
      background-color: rgba($color--gray-90, 0.34);
    }
  }

  &--green {
    color: $white;
    background-color: $color--green;
    border-color: $color--green;

    &:hover:enabled {
      background-color: darken($color--green, $hover-darken-percentage);
    }
  }

  &--green-transparent {
    color: $algae-green;
    background-color: rgba($algae-green, 0.24);
    border-color: rgba($algae-green, 0.24);

    &:hover:enabled {
      background-color: rgba($algae-green, 0.34);
    }
  }

  &--algae-green {
    color: $white;
    background-color: $algae-green;
    border-color: $algae-green;

    &:hover:enabled {
      background-color: darken($algae-green, $hover-darken-percentage);
    }
  }

  &--algae-green-transparent {
    color: $algae-green;
    background-color: rgba($algae-green, 0.24);
    border-color: rgba($algae-green, 0.24);

    &:hover:enabled {
      background-color: rgba($algae-green, 0.34);
    }
  }

  &--viridian {
    color: $white;
    background-color: $viridian;
    border-color: $viridian;

    &:hover:enabled {
      background-color: darken($viridian, $hover-darken-percentage);
    }
  }

  &--viridian-transparent {
    color: $viridian;
    background-color: rgba($viridian, 0.24);
    border-color: rgba($viridian, 0.24);

    &:hover:enabled {
      background-color: rgba($viridian, 0.34);
    }
  }

  &--pink {
    color: $white;
    background-color: $darkish-pink;
    border-color: $darkish-pink;

    &:hover:enabled {
      background-color: darken($darkish-pink, $hover-darken-percentage);
    }
  }

  &--pink-transparent {
    color: $darkish-pink;
    background-color: rgba($darkish-pink, 0.24);
    border-color: rgba($darkish-pink, 0.24);

    &:hover:enabled {
      background-color: rgba($darkish-pink, 0.34);
    }
  }

  &--darkish-pink {
    color: $white;
    background-color: $darkish-pink;
    border-color: $darkish-pink;

    &:hover:enabled {
      background-color: darken($darkish-pink, $hover-darken-percentage);
    }
  }

  &--darkish-pink-transparent {
    color: $darkish-pink;
    background-color: rgba($darkish-pink, 0.24);
    border-color: rgba($darkish-pink, 0.24);

    &:hover:enabled {
      background-color: rgba($darkish-pink, 0.34);
    }
  }

  &--white {
    color: var(--color-text-secondary);
    background-color: var(--color-general-white);
    border-color: $light-blue-grey-two;

    &:hover:enabled {
      filter: brightness(var(--hover-brightness));
    }
  }

  &--white-transparent {
    color: var(--color-text-secondary);
    background-color: rgba($white, 0.24);

    &:hover:enabled {
      background-color: rgba($white, 0.34);
    }
  }

  &--bluish-grey {
    color: $color--white;
    background-color: var(--color-text-third);
    border-color: var(--color-text-third);

    &:hover:enabled {
      background-color: darken($color--gray-60, $hover-darken-percentage);
    }
  }

  &--bluish-grey-transparent {
    color: var(--color-text-third);
    background-color: rgba($color--gray-60, 0.24);
    border-color: rgba($color--gray-60, 0.24);

    &:hover:enabled {
      background-color: rgba($color--gray-60, 0.34);
    }
  }

  &--bluey-purple {
    color: $color--white;
    background-color: $bluey-purple;
    border-color: $light-blue-grey-two;

    &:hover:enabled {
      background-color: darken($bluey-purple, $hover-darken-percentage);
    }
  }

  &--bluey-purple-transparent {
    color: $bluey-purple;
    background-color: rgba($bluey-purple, 0.24);
    border-color: rgba($bluey-purple, 0.24);

    &:hover:enabled {
      background-color: rgba($bluey-purple, 0.34);
    }
  }

  &--duck-egg-blue {
    color: var(--color-text-secondary);
    background-color: $duck-egg-blue;
    border-color: $light-blue-grey-two;

    &:hover:enabled {
      background-color: darken($duck-egg-blue, $hover-darken-percentage);
    }
  }

  &--duck-egg-blue-transparent {
    color: $duck-egg-blue;
    background-color: rgba($duck-egg-blue, 0.24);
    border-color: rgba($duck-egg-blue, 0.24);

    &:hover:enabled {
      background-color: rgba($duck-egg-blue, 0.34);
    }
  }

  &--light-peach {
    color: var(--color-text-secondary);
    background-color: $light-peach;
    border-color: $light-blue-grey-two;

    &:hover:enabled {
      background-color: darken($light-peach, $hover-darken-percentage);
    }
  }

  &--light-peach-transparent {
    color: $light-peach;
    background-color: rgba($light-peach, 0.24);
    border-color: rgba($light-peach, 0.24);

    &:hover:enabled {
      background-color: rgba($light-peach, 0.34);
    }
  }

  &--very-light-purple {
    color: var(--color-text-secondary);
    background-color: $very-light-purple;
    border-color: $light-blue-grey-two;

    &:hover:enabled {
      background-color: darken($very-light-purple, $hover-darken-percentage);
    }
  }

  &--very-light-purple-transparent {
    color: $very-light-purple;
    background-color: rgba($very-light-purple, 0.24);
    border-color: rgba($very-light-purple, 0.24);

    &:hover:enabled {
      background-color: rgba($very-light-purple, 0.34);
    }
  }

  &--brownish-orange {
    color: $white;
    background-color: $brownish-orange;
    border-color: $light-blue-grey-two;

    &:hover:enabled {
      background-color: darken($brownish-orange, $hover-darken-percentage);
    }
  }

  &--brownish-orange-transparent {
    color: $brownish-orange;
    background-color: rgba($brownish-orange, 0.24);
    border-color: rgba($brownish-orange, 0.24);

    &:hover:enabled {
      background-color: rgba($brownish-orange, 0.34);
    }
  }

  /* Types */
  &--default {
    position: relative;
    border: none;
    border-radius: 8px;

    &::before {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      display: block;
      content: '';
      border-radius: 8px;
      box-shadow: inset -2px -2px 0 0 rgba(38, 104, 116, 0.35);
    }
  }

  &--flat {
    border-style: solid;
    border-width: 1px;
    border-radius: 8px;
    box-shadow: none;

    &::before {
      display: none;
    }
  }

  &--circle {
    position: relative;
    border-width: 0;
    border-radius: 50%;

    &::before {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      display: block;
      content: '';
      border-radius: 50%;
    }
  }

  &--capsule {
    font-weight: 400;
    border-color: $light-blue-grey-two;
    border-style: solid;
    border-width: 1px;
  }

  &--text {
    font-size: 14px;
    font-weight: normal;
    color: $color--blue;
    text-decoration: none;
    background: transparent;
    border-width: 0;
    box-shadow: none;

    &:focus {
      outline: none;
    }

    &:hover:enabled {
      color: $slate;
      background: transparent;
    }

    &.dd-button--grey {
      color: var(--color-text-third);

      &:hover:enabled {
        color: $slate;
      }
    }
  }

  /* Sizes */
  &--tiny {
    height: 24px;
    padding: 0 12px;
    font-size: 12px;

    &.dd-button--circle {
      width: 24px;
      padding: 0;
    }

    &.dd-button--capsule {
      padding: 0 12px;
      border-radius: 14px;
    }
  }

  &--small {
    height: 28px;
    padding: 0 15px;
    font-size: 12px;

    &.dd-button--circle {
      width: 28px;
      padding: 0;
    }

    &.dd-button--capsule {
      padding: 0 12px;
      border-radius: 14px;
    }

    .dd-button__content {
      .new-icon {
        padding-left: 4px;
        font-size: 10px;
      }
    }
  }

  &--medium {
    height: 32px;
    line-height: 21px !important;
    padding: 0 15px;
    font-size: 12px;

    &.dd-button--circle {
      width: 32px;
      padding: 0;
    }

    &.dd-button--capsule {
      padding: 0 12px;
      border-radius: 16px;
    }
    .dd-button__content {
      .new-icon {
        padding-left: 4px;
        font-size: 10px;
      }
    }
  }

  &--large {
    height: 40px;
    line-height: 21px !important;
    padding: 0 20px;
    font-size: 14px;

    &.dd-button--circle {
      width: 40px;
      padding: 0;
    }

    &.dd-button--capsule {
      padding: 0 16px;
      border-radius: 20px;
    }

    .dd-button__content {
      .new-icon {
        padding-left: 4px;
        font-size: 12px;
      }
    }
  }

  /* Loading */
  &--loading {
    .dd-button__content {
      visibility: hidden;
    }

    .dd-button__loading-spinner {
      position: absolute;
    }
  }
  &--disabled {
    cursor: not-allowed;
    opacity: 0.8;
  }
}
</style>
